// src/components/ConversationSettingsModal.js
import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Card, Row, Col } from 'react-bootstrap';
import { FaCog, FaDownload, FaUpload, FaTrash, FaInfo } from 'react-icons/fa';
import ConversationStorageService from '../services/conversationStorageService';

const ConversationSettingsModal = ({ show, onHide, onSettingsChange }) => {
  const [storageInfo, setStorageInfo] = useState(null);
  const [preferences, setPreferences] = useState({});
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);
  const [alertType, setAlertType] = useState('info');

  useEffect(() => {
    if (show) {
      loadStorageInfo();
      loadPreferences();
    }
  }, [show]);

  const loadStorageInfo = () => {
    const info = ConversationStorageService.getStorageInfo();
    setStorageInfo(info);
  };

  const loadPreferences = () => {
    const prefs = ConversationStorageService.getUserPreferences();
    setPreferences(prefs);
  };

  const handlePreferenceChange = (key, value) => {
    const updatedPrefs = { ...preferences, [key]: value };
    setPreferences(updatedPrefs);
    ConversationStorageService.saveUserPreferences(updatedPrefs);
    
    if (onSettingsChange) {
      onSettingsChange(updatedPrefs);
    }
  };

  const handleExportData = () => {
    try {
      const data = ConversationStorageService.exportConversations();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      a.download = `conversation-backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      showAlert('Conversation data exported successfully!', 'success');
    } catch (error) {
      console.error('Export error:', error);
      showAlert('Failed to export data. Please try again.', 'danger');
    }
  };

  const handleImportData = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        const result = ConversationStorageService.importConversations(data);
        
        showAlert(
          `Import successful! ${result.imported} conversations imported, ${result.duplicatesSkipped} duplicates skipped.`,
          'success'
        );
        
        loadStorageInfo();
        
        if (onSettingsChange) {
          onSettingsChange(preferences);
        }
      } catch (error) {
        console.error('Import error:', error);
        showAlert('Failed to import data. Please check the file format.', 'danger');
      }
    };
    reader.readAsText(file);
    
    // Reset file input
    event.target.value = '';
  };

  const handleClearAllData = () => {
    if (showClearConfirm) {
      try {
        ConversationStorageService.clearAllData();
        showAlert('All conversation data cleared successfully!', 'success');
        loadStorageInfo();
        setShowClearConfirm(false);
        
        if (onSettingsChange) {
          onSettingsChange({ ...preferences, dataCleared: true });
        }
      } catch (error) {
        console.error('Clear data error:', error);
        showAlert('Failed to clear data. Please try again.', 'danger');
      }
    } else {
      setShowClearConfirm(true);
      setTimeout(() => setShowClearConfirm(false), 5000); // Auto-cancel after 5 seconds
    }
  };

  const handleCleanupOld = () => {
    try {
      const result = ConversationStorageService.cleanupOldConversations(30);
      if (result.removedCount > 0) {
        showAlert(
          `Cleanup complete! Removed ${result.removedCount} old conversations. ${result.remainingCount} conversations remaining.`,
          'success'
        );
        loadStorageInfo();
      } else {
        showAlert('No old conversations found to clean up.', 'info');
      }
    } catch (error) {
      console.error('Cleanup error:', error);
      showAlert('Failed to cleanup old conversations.', 'danger');
    }
  };

  const showAlert = (message, type) => {
    setAlertMessage(message);
    setAlertType(type);
    setTimeout(() => setAlertMessage(null), 5000);
  };

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      size="lg" 
      centered
      backdrop="static"
      style={{ marginTop: '60px' }}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <FaCog className="me-2" />
          Conversation Settings
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {alertMessage && (
          <Alert variant={alertType} dismissible onClose={() => setAlertMessage(null)}>
            {alertMessage}
          </Alert>
        )}

        <Row>
          <Col md={6}>
            <Card className="mb-3">
              <Card.Header>
                <h6 className="mb-0">Interface Preferences</h6>
              </Card.Header>
              <Card.Body>
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      label="Show conversations sidebar by default"
                      checked={preferences.showConversationsList}
                      onChange={(e) => handlePreferenceChange('showConversationsList', e.target.checked)}
                    />
                  </Form.Group>
                  
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      label="Show tasks panel by default"
                      checked={preferences.showTasks}
                      onChange={(e) => handlePreferenceChange('showTasks', e.target.checked)}
                    />
                  </Form.Group>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Default task filter</Form.Label>
                    <Form.Select
                      value={preferences.taskFilter}
                      onChange={(e) => handlePreferenceChange('taskFilter', e.target.value)}
                    >
                      <option value="all">All Tasks</option>
                      <option value="created">Created</option>
                      <option value="executing">Executing</option>
                      <option value="completed">Completed</option>
                      <option value="failed">Failed</option>
                    </Form.Select>
                  </Form.Group>
                </Form>
              </Card.Body>
            </Card>
          </Col>
          
          <Col md={6}>
            <Card className="mb-3">
              <Card.Header>
                <h6 className="mb-0">
                  <FaInfo className="me-2" />
                  Storage Information
                </h6>
              </Card.Header>
              <Card.Body>
                {storageInfo ? (
                  <div>
                    <p className="mb-2">
                      <strong>Conversations:</strong> {storageInfo.conversationsCount}
                    </p>
                    <p className="mb-2">
                      <strong>Storage Used:</strong> {storageInfo.totalSize}
                    </p>
                    <small className="text-muted">
                      Conversations: {storageInfo.conversationsSize}<br/>
                      Cached Messages: {storageInfo.messagesSize}
                    </small>
                  </div>
                ) : (
                  <p className="text-muted">Loading storage information...</p>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Card>
          <Card.Header>
            <h6 className="mb-0">Data Management</h6>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <div className="d-grid gap-2">
                  <Button variant="outline-primary" onClick={handleExportData}>
                    <FaDownload className="me-2" />
                    Export Data
                  </Button>
                  
                  <Form.Group>
                    <Form.Label as={Button} variant="outline-secondary" className="w-100 mb-0">
                      <FaUpload className="me-2" />
                      Import Data
                      <Form.Control
                        type="file"
                        accept=".json"
                        onChange={handleImportData}
                        style={{ display: 'none' }}
                      />
                    </Form.Label>
                  </Form.Group>
                  
                  <Button variant="outline-warning" onClick={handleCleanupOld}>
                    Clean Old Conversations
                  </Button>
                </div>
              </Col>
              
              <Col md={6}>
                <div className="d-grid">
                  <Button 
                    variant={showClearConfirm ? "danger" : "outline-danger"}
                    onClick={handleClearAllData}
                  >
                    <FaTrash className="me-2" />
                    {showClearConfirm ? "Confirm Clear All Data" : "Clear All Data"}
                  </Button>
                  
                  {showClearConfirm && (
                    <small className="text-danger mt-2">
                      Click again to confirm deletion of all conversations and data.
                      This action cannot be undone.
                    </small>
                  )}
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Modal.Body>
      
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ConversationSettingsModal;
